import React, { FC, useEffect } from 'react'

import { Grid, useTheme } from '@mui/material'
import { isEmpty, isEqual } from 'lodash'
import { useTranslation } from 'react-i18next'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { fetchRiskReport } from '../ReportsSlice'

import { defaultRiskData } from './defaultRiskData'
import { RiskTabWidget } from './RiskTabWidget'

export const RiskManagement: FC = () => {
  const { spacing } = useTheme()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const { risk } = useAppSelector(({ reports }) => reports)
  const { period, currentAccount } = useAppSelector(({ userAccounts }) => userAccounts)
  const data = risk.data || defaultRiskData
  const riskLabelsMapping = {
    'Use of stop-loss': {
      question: t('useOfStopLoss', 'Use of stop-loss'),
      positiveLabel: t('used', 'Used'),
      negativeLabel: t('notUsed', 'Not used'),
    },
    'Predetermined risks': {
      question: t('predeterminedRisks', 'Predetermined risks'),
      positiveLabel: t('predetermined', 'Predetermined'),
      negativeLabel: t('notPredetermined', 'Not predetermined'),
    },
    'Risk-Reward strategy': {
      question: t('riskRewardStrategy', 'Risk-Reward strategy'),
      positiveLabel: t('followed', 'Followed'),
      negativeLabel: t('notFollowed', 'Not followed'),
    },
    'Limited correlation': {
      question: t('limitedCorrelation', 'Limited correlation'),
      positiveLabel: t('correlatedTransactions', 'Correlated transactions'),
      negativeLabel: t('uncorrelatedTransactions', 'Uncorrelated transactions'),
    },
    'Transaction size analysis': {
      question: t('transactionSizeAnalysis', 'Transaction size analysis'),
      positiveLabel: t('analysisMade', 'Analysis made'),
      negativeLabel: t('analysisNotMade', 'Analysis not made'),
    },
  } as const
  const explanationsMapping = {
    'Use of stop-loss': t(
      'theGraphRepresentsForWhichPercentOfTransactionsStopLoss',
      'The graph represents for which percent of transactions the stop-loss has been used and for which not',
    ),
    'Predetermined risks': t(
      'theGraphRepresentsProportionPredeterminedRisk',
      'The graph represents the proportion of predetermined risk and not predetermined risk transactions',
    ),
    'Risk-Reward strategy': t(
      'theGraphRepresentsForWhichPercentOfTransactionsRRiskRewardStrategy',
      'The graph represents for which percent of transactions the Risk-Reward strategy has been followed and for which not',
    ),
    'Limited correlation': t(
      'theGraphRepresentsProportionUncorrelatedAndCorrelatedTransactions',
      'The graph represents the proportion of uncorrelated and correlated transactions',
    ),
    'Transaction size analysis': t(
      'theGraphRepresentsForWhichPercentOfTransactionsSizeAnalysisMade',
      'The graph represents for which percent of transactions the size analysis has been made and for which not',
    ),
  }

  useEffect(() => {
    const periodsIsEqual = risk?.meta?.period && period && isEqual(risk.meta.period, period)
    const isNeedToUpdate =
      isEmpty(risk) || !periodsIsEqual || currentAccount?.id !== risk?.meta.account

    if (currentAccount?.id && isNeedToUpdate) {
      dispatch(
        fetchRiskReport({
          account: currentAccount?.id,
          period: period,
        }),
      )
    }
  }, [period, currentAccount, risk])

  return (
    <Grid container spacing={2}>
      {data.map(({ question, positiveLabel, negativeLabel, perCent }) => (
        <Grid
          key={question}
          item
          xs={12}
          md={6}
          lg={4}
          xl={3}
          sx={{
            pageBreakInside: 'avoid',
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: spacing(35), sm: spacing(38), md: spacing(42.25) },
          }}
        >
          <RiskTabWidget
            label={
              riskLabelsMapping[question as keyof typeof riskLabelsMapping]?.question || question
            }
            description={explanationsMapping[question as keyof typeof explanationsMapping]}
            positiveValue={perCent}
            positiveLabel={
              riskLabelsMapping[question as keyof typeof riskLabelsMapping]?.positiveLabel ||
              positiveLabel
            }
            negativeValue={100 - perCent}
            negativeLabel={
              riskLabelsMapping[question as keyof typeof riskLabelsMapping]?.negativeLabel ||
              negativeLabel
            }
          />
        </Grid>
      ))}
    </Grid>
  )
}
